package it.neckar.lizergy.model.configuration.components

import it.neckar.lizergy.model.configuration.components.ElectricityWorkConfiguration.ElectricityWorkConfigurationId
import it.neckar.lizergy.model.configuration.quote.builder.InverterType
import it.neckar.lizergy.model.configuration.quote.builder.Manufacturer
import it.neckar.lizergy.model.configuration.quote.builder.ResolvedInverterSelection
import it.neckar.lizergy.model.financial.currency.ValueAddedTax
import it.neckar.uuid.HasUuid
import it.neckar.uuid.NativeUuid
import it.neckar.uuid.randomUuid4
import kotlinx.serialization.Serializable
import kotlin.uuid.Uuid

interface ElectricityWorkConfiguration : HasUuid {
  val id: ElectricityWorkConfigurationId
  val electricityWorkEffort: ConfigurationItem
  val sunnyHomeManager: Boolean
  val neuerZaehlerschrank: Boolean
  val enwg14aIntegration: EnWG14aIntegrationType
  val zusammenZuLegendeZaehlerNummern: List<String>
  val einbauUnterverteiler: Boolean
  val manualEinbauDigitalerZaehler: Int?
  val smaEnergyMeter: Boolean
  val slsNachruesten: Boolean
  val ueberspannungsschutzAC: UeberspannungsschutzACType

  override val uuid: Uuid
    get() = id.uuid

  val einbauDigitalerZaehler: Int
    get() = manualEinbauDigitalerZaehler ?: zusammenZuLegendeZaehlerNummern.size

  fun sunnyHomeManager(selectedBattery: BatteryConfiguration?, waermepumpeSelected: Boolean, intelligentWallboxSelected: Boolean, heizstabSelected: Boolean): Boolean {
    return selectedBattery == null && (sunnyHomeManager || waermepumpeSelected || intelligentWallboxSelected || heizstabSelected)
  }

  fun homeManagerNote(selectedBattery: BatteryConfiguration?, waermepumpeSelected: Boolean, intelligentWallboxSelected: Boolean, heizstabSelected: Boolean): String {
    val homeManagerName = when (selectedBattery?.battery?.manufacturer) {
      Manufacturer.SMA -> "Sunny Home Manager 2.0"
      Manufacturer.IBC -> "IBC HomeOne Hub BT-RE1"
      null -> null
      else -> throw NotImplementedError("Unknown battery manufacturer: ${selectedBattery.battery.manufacturer}")
    }
    return if (selectedBattery != null) {
      "$homeManagerName ist bereits im Batteriesystem enthalten."
    } else {
      buildList {
        if (waermepumpeSelected) add("Wärmepumpe")
        if (intelligentWallboxSelected) add("intelligente Wallbox")
        if (heizstabSelected) add("Heizstab")
      }.joinToString(", ", "$homeManagerName muss ausgewählt sein, da ", " ausgewählt sind.")
    }
  }


  @Serializable
  data class ElectricityWorkConfigurationId(val uuid: NativeUuid) {

    override fun toString(): String {
      return uuid.toString()
    }

    fun format(): String {
      return uuid.toString()
    }

    companion object {
      fun random(): ElectricityWorkConfigurationId {
        return ElectricityWorkConfigurationId(randomUuid4())
      }
    }
  }
}


@Serializable
data class ResolvedElectricityWorkConfiguration(
  override val id: ElectricityWorkConfigurationId,
  override val electricityWorkEffort: ConfigurationItem,
  override val sunnyHomeManager: Boolean,
  val independenceManager: IndependenceManagerType?,
  override val neuerZaehlerschrank: Boolean,
  override val enwg14aIntegration: EnWG14aIntegrationType,
  override val zusammenZuLegendeZaehlerNummern: List<String>,
  override val einbauUnterverteiler: Boolean,
  override val manualEinbauDigitalerZaehler: Int?,
  override val smaEnergyMeter: Boolean,
  override val slsNachruesten: Boolean,
  override val ueberspannungsschutzAC: UeberspannungsschutzACType = UeberspannungsschutzACType.Standard,
) : ElectricityWorkConfiguration {

  fun invalidManagerCombination(inverterSelection: ResolvedInverterSelection, batteryInverter: InverterType?): Boolean? {
    return independenceManager?.invalidManagerCombination(inverterSelection, batteryInverter)
  }

  fun duplicate(mapOfOldToNewUuids: MutableMap<Uuid, Uuid>): ResolvedElectricityWorkConfiguration {
    val newId = ElectricityWorkConfigurationId.random()
    mapOfOldToNewUuids[id.uuid] = newId.uuid
    return copy(id = newId)
  }

  companion object {
    fun getEmpty(id: ElectricityWorkConfigurationId = ElectricityWorkConfigurationId.random(), configurationItemId: ConfigurationItem.ConfigurationItemId = ConfigurationItem.ConfigurationItemId.random()): ResolvedElectricityWorkConfiguration {
      return ResolvedElectricityWorkConfiguration(
        id = id,
        electricityWorkEffort = ConfigurationItem.getEmpty(id = configurationItemId, valueAddedTax = ValueAddedTax.zero),
        sunnyHomeManager = false,
        independenceManager = null,
        neuerZaehlerschrank = false,
        enwg14aIntegration = EnWG14aIntegrationType.None,
        zusammenZuLegendeZaehlerNummern = emptyList(),
        einbauUnterverteiler = false,
        manualEinbauDigitalerZaehler = null,
        smaEnergyMeter = false,
        slsNachruesten = false,
        ueberspannungsschutzAC = UeberspannungsschutzACType.Standard,
      )
    }
  }
}
